/**
 * To order: the buying list. Three tabs, one rule each (lib/supply.ts):
 *   Order now   — will run out before a new order could arrive
 *   Ship sooner — already on order, but the shelf empties before it lands
 *   Top-ups     — only below the safety buffer; can wait
 *   Gold Crest  — the separate Texas company: 60 days of use, minus stock and open orders (components/GoldCrest.tsx)
 * Ticking parts lets you adjust quantities for an Excel export. Nothing is written anywhere.
 */
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { downloadCsv } from "../lib/csv.ts";
import { partName } from "../lib/skuNames.ts";
import { isYards } from "../lib/categories.ts";
import { fmt, looksHigh, NOT_STOCKED_NOTE, refLabel, shortDate, unitOf, useSupply, type Row, type SpeedUp } from "../lib/supply.ts";
import { usePanel } from "../components/DetailPanel.tsx";
import { StockTimeline, TimelineHeader, stockStory } from "../components/StockTimeline.tsx";
import { CategoryTabs } from "../components/CategoryTabs.tsx";
import { ExportButton, ListBar } from "../components/ListBar.tsx";
import { GoldCrestTable, useGoldCrest } from "../components/GoldCrest.tsx";
import { SHOW_GOLD_CREST } from "../lib/flags.ts";

const ALL_TABS = ["now", "sooner", "topups", "gc"] as const;
// Gold Crest is hidden until the March Products pages are done (lib/flags.ts).
const TABS = ALL_TABS.filter((t) => t !== "gc" || SHOW_GOLD_CREST);
const TAB_LABEL: Record<Tab, string> = { now: "Order now", sooner: "Ship sooner", topups: "Top-ups", gc: "Gold Crest" };
const matches = (words: string[], id: string) => words.every((w) => `${id} ${partName(id)}`.toLowerCase().includes(w));

type Tab = "now" | "sooner" | "topups" | "gc";
const DESC: Record<Tab, string> = {
  now: "Parts that will run out before a new order could arrive.",
  sooner: "Already on order, but arriving after the shelf runs empty. Ask the supplier to ship sooner.",
  topups: "Below safety stock, but they'll last until a new order arrives.",
  gc: "Gold Crest (Texas) parts that won't last 60 days at the last 30 days' pace, counting open orders.",
};

export function OrderPage() {
  const s = useSupply();
  const [params, setParams] = useSearchParams();
  const asked = params.get("tab") as Tab | null;
  const tab: Tab = asked && (TABS as readonly string[]).includes(asked) ? asked : "now";
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    if (t === "now") next.delete("tab");
    else next.set("tab", t);
    setParams(next);
  };

  const [showHidden, setShowHidden] = useState(false);
  const gc = useGoldCrest(SHOW_GOLD_CREST);
  if (s.error) return <div className="u-error">Couldn't load the buying list: {s.error.message}</div>;
  const now = showHidden ? s.orderNowAll : s.orderNow;
  const tops = showHidden ? s.topUpsAll : s.topUps;
  const hiddenN = tab === "now" ? s.orderNowAll.length - s.orderNow.length : tab === "topups" ? s.topUpsAll.length - s.topUps.length : 0;

  return (
    <div className="u-page">
      <CategoryTabs
        items={TABS}
        value={tab}
        onChange={setTab}
        label={(t) => TAB_LABEL[t]}
        counts={{ now: now.length, sooner: s.speedUps.length, topups: tops.length, gc: gc.rows.length }}
        loading={s.loading || gc.loading}
      />
      <p className="page-note">
        {DESC[tab]}
        {!s.loading && hiddenN > 0 && !showHidden && <> {hiddenN} more parts {NOT_STOCKED_NOTE}. <button type="button" className="btn link" onClick={() => setShowHidden(true)}>Show them</button></>}
        {showHidden && <> Including parts Operations doesn't buy for stock. <button type="button" className="btn link" onClick={() => setShowHidden(false)}>Hide them again</button></>}
      </p>
      {tab === "gc" && SHOW_GOLD_CREST ? (
        gc.loading ? <div className="tablewrap"><div className="u-empty">Loading…</div></div> : <GoldCrestTable gc={gc} />
      ) : s.loading ? (
        <div className="tablewrap"><div className="u-empty">Loading…</div></div>
      ) : tab === "sooner" ? (
        <SoonerTable rows={s.speedUps} />
      ) : (
        <BuyTable key={tab} rows={tab === "now" ? now : tops} tab={tab} />
      )}
    </div>
  );
}

function BuyTable({ rows, tab }: { rows: Row[]; tab: Tab }) {
  const { open } = usePanel();
  const [ticked, setTicked] = useState<Record<string, number>>({});
  const [filter, setFilter] = useState("");
  const words = filter.toLowerCase().split(/\s+/).filter(Boolean);
  const sorted = useMemo(
    () => [...rows].filter((r) => matches(words, r.itemid)).sort((a, b) => (a.days_cover_on_hand ?? 1e9) - (b.days_cover_on_hand ?? 1e9)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, filter],
  );
  const groups = [
    { label: "Overseas", unit: "units", rows: sorted.filter((r) => !isYards(r.item_type)) },
    { label: "Fabric and trim", unit: "yards", rows: sorted.filter((r) => isYards(r.item_type)) },
  ].filter((g) => g.rows.length);
  const tickedSkus = Object.keys(ticked);
  const tickedUnits = tickedSkus.reduce((t, k) => t + (ticked[k] || 0), 0);

  const toggle = (r: Row) =>
    setTicked((t) => {
      const next = { ...t };
      if (r.itemid in next) delete next[r.itemid];
      else next[r.itemid] = Math.round(r.need_to_order ?? 0);
      return next;
    });
  const toggleGroup = (rs: Row[], on: boolean) =>
    setTicked((t) => {
      const next = { ...t };
      for (const r of rs) {
        if (on) next[r.itemid] = next[r.itemid] ?? Math.round(r.need_to_order ?? 0);
        else delete next[r.itemid];
      }
      return next;
    });

  const exportRows = () => {
    const pick = tickedSkus.length ? sorted.filter((r) => r.itemid in ticked) : sorted;
    downloadCsv(`to-order-${tab}-${new Date().toISOString().slice(0, 10)}.csv`, pick.map((r) => ({
      Part: r.itemid,
      Name: partName(r.itemid),
      "On hand": Math.round(r.supply.comp_available ?? 0),
      "Coming in time": Math.round(r.supply.incoming_within_lead ?? 0),
      "Will use before new stock arrives": Math.round(r.lead_forecast ?? 0),
      "Safety stock": Math.round(r.safety_qty ?? 0),
      "Need to order": Math.round(r.need_to_order ?? 0),
      "Qty to order": r.itemid in ticked ? ticked[r.itemid] : Math.round(r.need_to_order ?? 0),
      Unit: unitOf(r),
    })));
  };

  if (!rows.length) return <div className="tablewrap"><div className="u-empty">Nothing here today.</div></div>;

  return (
    <>
      <ListBar title={`${TAB_LABEL[tab]} · ${sorted.length}`} search={filter} onSearch={setFilter}>
        {tickedSkus.length > 0 && (
          <>
            <span className="tick-sum">{tickedSkus.length} ticked · {fmt(tickedUnits)} units</span>
            <button type="button" className="btn link" onClick={() => setTicked({})}>Clear</button>
          </>
        )}
        <ExportButton onClick={exportRows} />
      </ListBar>

      <div className="tablewrap">
        <table className="dt left">
          <thead>
            <tr>
              <th>Part</th>
              <th className="l hide-m"><TimelineHeader /></th>
              <th>On hand</th>
              <th className="hide-m">Coming in time</th>
              <th className="hide-m">Will use before<br />new stock arrives</th>
              <th className="hide-m">Safety stock</th>
              <th>Need to order</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const allOn = g.rows.every((r) => r.itemid in ticked);
              return (
                <GroupRows key={g.label}>
                  <tr className="grp">
                    <td className="l" colSpan={7}>
                      <label className="pt" style={{ alignItems: "center" }}>
                        <input type="checkbox" className="cb" style={{ margin: 0 }} checked={allOn} onChange={() => toggleGroup(g.rows, !allOn)} aria-label={`Tick all ${g.label}`} />
                        <span>{g.label} <span className="sub">· {g.rows.length} parts · {g.unit}</span></span>
                      </label>
                    </td>
                  </tr>
                  {g.rows.map((r) => {
                    const on = r.itemid in ticked;
                    return (
                      <tr key={r.itemid} className={on ? "sel" : ""}>
                        <td className="l">
                          <div className="pt">
                            <input type="checkbox" className="cb" checked={on} onChange={() => toggle(r)} aria-label={`Tick ${r.itemid}`} />
                            <span>
                              <button type="button" className="code" onClick={() => open("part", r.itemid)}>{r.itemid}</button>
                              {partName(r.itemid) && <span className="name">{partName(r.itemid)}</span>}
                              <span className="m-only sub">{stockStory(r).text}</span>
                            </span>
                          </div>
                        </td>
                        <td className="l hide-m"><StockTimeline r={r} unit={unitOf(r)} /></td>
                        <td>{fmt(r.supply.comp_available)}</td>
                        <td className="hide-m">{fmt(r.supply.incoming_within_lead)}</td>
                        <td className="hide-m">{fmt(r.lead_forecast)}</td>
                        <td className="hide-m">{fmt(r.safety_qty)}</td>
                        <td>
                          {on ? (
                            <>
                              <input
                                className="qty"
                                inputMode="numeric"
                                value={ticked[r.itemid]}
                                aria-label={`Quantity to export for ${r.itemid}`}
                                onChange={(e) => setTicked((t) => ({ ...t, [r.itemid]: Math.max(0, Number(e.target.value.replace(/\D/g, "")) || 0) }))}
                              />
                              <span className="was">qty to export</span>
                            </>
                          ) : (
                            <>
                              <b>{fmt(r.need_to_order)}</b>
                              {looksHigh(r) && <span className="chk" title="Safety stock is more than the part is expected to use. Open the part to see why.">mostly safety stock · check before ordering</span>}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </GroupRows>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="fine">
        Tick parts to choose quantities for the export; with nothing ticked, Export takes the whole list. Nothing here changes NetSuite.
        "Will use" covers the time new stock takes to arrive (each part's lead time). Runs out soonest first.
      </p>
    </>
  );
}

function GroupRows({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function SoonerTable({ rows: all }: { rows: SpeedUp[] }) {
  const { open } = usePanel();
  const [filter, setFilter] = useState("");
  const words = filter.toLowerCase().split(/\s+/).filter(Boolean);
  const rows = words.length
    ? all.map((s) => ({ ...s, parts: s.parts.filter((p) => matches(words, p.row.itemid)) })).filter((s) => s.parts.length || words.every((w) => s.ref.toLowerCase().includes(w)))
    : all;
  if (!all.length) return <div className="tablewrap"><div className="u-empty">Nothing is arriving too late.</div></div>;
  const exportRows = () =>
    downloadCsv(`ship-sooner-${new Date().toISOString().slice(0, 10)}.csv`, rows.flatMap((s) => s.parts.map((p) => ({
      "PO or container": refLabel(s.ref, s.kind),
      Part: p.row.itemid,
      Name: partName(p.row.itemid),
      "Shelf empty": p.emptyFrom,
      Arrives: p.arrives,
      "Days with no stock": p.daysEmpty,
    }))));
  return (
    <>
      <ListBar title={`Ship sooner · ${rows.length} ${rows.length === 1 ? "delivery" : "deliveries"}`} search={filter} onSearch={setFilter} placeholder="Filter parts or POs">
        <ExportButton onClick={exportRows} />
      </ListBar>
      <div className="tablewrap">
        <table className="dt left">
          <thead>
            <tr>
              <th>PO · part</th>
              <th className="l hide-m">Shelf empty</th>
              <th className="l hide-m">Arrives</th>
              <th>Days with<br />no stock</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <GroupRows key={s.ref}>
                <tr className="grp click" onClick={() => open(s.kind === "po" ? "po" : "ca", s.ref)}>
                  <td className="l">{refLabel(s.ref, s.kind)}{s.kind === "container" && <span className="sub"> container</span>}</td>
                  <td className="hide-m" colSpan={2} />
                  <td><span className="btn sm">Open ›</span></td>
                </tr>
                {s.parts.map((p) => (
                  <tr key={p.row.itemid} className="click" onClick={() => open("part", p.row.itemid)}>
                    <td className="l ind">
                      <span className="code">{p.row.itemid}</span>
                      {partName(p.row.itemid) && <span className="name">{partName(p.row.itemid)}</span>}
                      <span className="m-only sub">Empty from {shortDate(p.emptyFrom)} · arrives {shortDate(p.arrives)}</span>
                    </td>
                    <td className="l hide-m">{shortDate(p.emptyFrom)}</td>
                    <td className="l hide-m">{shortDate(p.arrives)}</td>
                    <td><b>{p.daysEmpty}</b></td>
                  </tr>
                ))}
              </GroupRows>
            ))}
          </tbody>
        </table>
      </div>
      <p className="fine">Open a PO to copy its details for an email to the supplier. Nothing here changes NetSuite.</p>
    </>
  );
}
